import React, { useRef } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

function formatCurrency(amount) {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}

function formatInvoiceDate(date) {
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

const InvoicePage = () => {
  const invoiceRef = useRef();
  const handlePdf = async () => {
    const input = invoiceRef.current;
    const { month, year } = location.state || {};
    const m = String(month ?? new Date().getMonth() + 1).padStart(2, '0');
    const y = String(year ?? new Date().getFullYear()).slice(-2);
    const filename = `Sanduke-Invoice-${m}${y}.pdf`;

    const canvas = await html2canvas(input, { scale: 1.5 });
    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(filename);
  };
  const location = useLocation();
  const navigate = useNavigate();
  const { month, year, day, pagesScanned } = location.state || {};

  // Invoice number: YYMM (e.g., 250602 for June 2, 2025)
  const invoiceNumber = `${String(year).slice(-2)}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`;
  const dateIssued = `${month}/${day}/${year}`;
  const issuedDate = new Date(Number(year), Number(month) - 1, Number(day));
  const dueDate = new Date(issuedDate);
  dueDate.setDate(dueDate.getDate() + 30);

  // Invoice rows
  const scannedPages = pagesScanned || 0;
  const monthNum = Number(month);
  const yearNum = Number(year);
  const isAug2025OrLater = (yearNum > 2025) || (yearNum === 2025 && monthNum >= 8);
  const isJune2026OrLater = (yearNum > 2026) || (yearNum === 2026 && monthNum >= 6);

  // Rates by billing period: pre-Aug 2025 $0.12/page; Aug 2025–May 2026 $0.17/page + $2,000 storage;
  // June 2026+ +1.5% on both ($0.17255/page, $2,030 storage)
  let monthlyStorageFee = 2000;
  let scannedPageRate = 0.12;
  if (isJune2026OrLater) {
    monthlyStorageFee = 2030;
    scannedPageRate = 0.17255;
  } else if (isAug2025OrLater) {
    scannedPageRate = 0.17;
  }
  const scannedPagesTotal = scannedPages * scannedPageRate;


  const subtotal = monthlyStorageFee + scannedPagesTotal;
  const totalDue = subtotal;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, maxWidth: 700, mx: 'auto', mt: 4 }}>
        <Button variant="outlined" color="primary" onClick={handlePdf} sx={{ mb: 2 }}>
          Download PDF
        </Button>
        <Button variant="contained" sx={{ mb: 2 }} onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
      <Box ref={invoiceRef} sx={{ maxWidth: 700, mx: 'auto', my: 2, p: 4, background: 'white', borderRadius: 2, boxShadow: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
          <Typography variant="subtitle1" fontWeight={500}>INVOICE # {invoiceNumber}</Typography>
          <Typography variant="subtitle2">DATE ISSUED: {dateIssued}</Typography>
        </Box>
        <Box textAlign="right">
          <Typography variant="h5" fontWeight={700} color="primary.dark">Sanduke</Typography>
          <Typography variant="h4" fontWeight={300} color="primary.main">INVOICE</Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" sx={{ mt: 2 }}>Issued to:</Typography>
      <Typography variant="body1">Carepoint Health</Typography>
      <Divider sx={{ my: 2 }} />
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Qty</TableCell>
              <TableCell>Service Name</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>1</TableCell>
              <TableCell>Monthly Storage Fee</TableCell>
              <TableCell align="right">{formatCurrency(monthlyStorageFee)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{scannedPages.toLocaleString()}</TableCell>
              <TableCell>Pages scanned</TableCell>
              <TableCell align="right">{formatCurrency(scannedPagesTotal)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, px: 2, py: 1, borderRadius: 1 }}>
          <Typography variant="subtitle1">Subtotal</Typography>
          <Typography variant="subtitle1">{formatCurrency(subtotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, background: '#1a3553', color: 'white', px: 2, py: 1, borderRadius: 1, mt: 1 }}>
          <Typography variant="subtitle1">Total Due</Typography>
          <Typography variant="subtitle1">{formatCurrency(totalDue)}</Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" sx={{ mt: 2 }}>Please pay by ACH:</Typography>
      <Typography variant="body2">Routing: 091311229</Typography>
      <Typography variant="body2">Account Number: 202525008659</Typography>
      <Typography variant="body2" sx={{ mt: 2 }}>Terms: Net 30</Typography>
      <Typography variant="body2">Due date: {formatInvoiceDate(dueDate)}</Typography>
      <Divider sx={{ my: 4 }} />
      <Typography align="center" variant="body1">Thank you for your business!</Typography>
      <Typography align="center" variant="subtitle2" fontWeight={700}>Sanduke</Typography>

    </Box>
    </>
  );
};

export default InvoicePage;
